import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { TypeJour } from './calendrier-utils';

/**
 * Événements de calendrier saisis à la main, conservés en base et rattachés au
 * compte. Ils remplacent le stockage local : ils suivent l'utilisateur d'un
 * poste à l'autre, entrent dans la sauvegarde, et surtout leurs droits sont
 * tenus par l'API — l'écran ne fait que refléter ce qu'elle autorise.
 */

export type CategorieEvenement =
  | 'MES_FORMATIONS'
  | 'REUNIONS'
  | 'EXAMENS_EMARGEMENTS'
  | 'PERSONNEL';

export interface EvenementCalendrier {
  id: string;
  titre: string;
  categorie: CategorieEvenement;
  /** Date civile locale (Indian/Reunion), format YYYY-MM-DD. */
  date: string;
  heureDebut: string | null;
  heureFin: string | null;
  lieu: string | null;
}

export interface EntreeEvenement {
  titre: string;
  categorie: CategorieEvenement;
  date: string;
  heureDebut?: string | null;
  heureFin?: string | null;
  lieu?: string | null;
}

interface ReponseEvenements {
  evenements: EvenementCalendrier[];
  /** Catégories que l'API accepte de ce profil — l'écran n'en propose pas d'autres. */
  categoriesAutorisees: CategorieEvenement[];
  /**
   * Ce profil peut-il ouvrir le calendrier d'un tiers ? Décidé par l'API, pas
   * par l'écran : le sélecteur de personne n'apparaît que si elle l'accorde.
   */
  consultationAutorisee: boolean;
}

export type TypePersonne = 'APPRENANT' | 'UTILISATEUR';

export interface PersonneCalendrier {
  type: TypePersonne;
  id: string;
  nom: string;
  role: string;
  siteCode: string | null;
}

export interface EvenementConsulte extends EvenementCalendrier {
  /** `PLATEFORME` : séance ou échéance. `SAISI` : saisi par la personne. */
  source: 'PLATEFORME' | 'SAISI';
  /** Type exact enregistré dans la programmation de `/adm-formations`. */
  typeJour?: TypeJour;
}

export interface CalendrierConsulte {
  personne: PersonneCalendrier;
  evenements: EvenementConsulte[];
  /** Événements personnels de la personne, comptés mais jamais rendus. */
  personnelsMasques: number;
}

const RACINE = ['calendrier', 'evenements'] as const;

export function useEvenementsCalendrier(debut: string, fin: string) {
  return useQuery({
    queryKey: [...RACINE, debut, fin],
    queryFn: () =>
      appelApi<ReponseEvenements>(
        `/calendrier/evenements?debut=${encodeURIComponent(debut)}&fin=${encodeURIComponent(fin)}`,
      ),
  });
}

function invalider(client: ReturnType<typeof useQueryClient>): void {
  client.invalidateQueries({ queryKey: RACINE });
}

export function useCreerEvenement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entree: EntreeEvenement) =>
      appelApi<EvenementCalendrier>('/calendrier/evenements', { methode: 'POST', corps: entree }),
    onSuccess: () => invalider(client),
  });
}

export function useModifierEvenement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, entree }: { id: string; entree: EntreeEvenement }) =>
      appelApi<EvenementCalendrier>(`/calendrier/evenements/${id}`, {
        methode: 'PATCH',
        corps: entree,
      }),
    onSuccess: () => invalider(client),
  });
}

export function useSupprimerEvenement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<void>(`/calendrier/evenements/${id}`, { methode: 'DELETE' }),
    onSuccess: () => invalider(client),
  });
}

/**
 * Annuaire des calendriers consultables. L'API le borne aux sites du compte
 * appelant et refuse les profils qui n'y ont pas droit — la requête n'est donc
 * lancée que lorsque l'écran sait la consultation ouverte.
 */
export function usePersonnesCalendrier(
  filtres: { recherche: string; type: TypePersonne | null; site: string },
  activee: boolean,
) {
  const parametres = new URLSearchParams();
  if (filtres.recherche.trim()) parametres.set('recherche', filtres.recherche.trim());
  if (filtres.type) parametres.set('type', filtres.type);
  if (filtres.site) parametres.set('site', filtres.site);
  const suffixe = parametres.toString() ? `?${parametres.toString()}` : '';

  return useQuery({
    queryKey: [...RACINE, 'personnes', filtres.recherche.trim(), filtres.type, filtres.site],
    queryFn: () =>
      appelApi<{ personnes: PersonneCalendrier[] }>(`/calendrier/personnes${suffixe}`),
    enabled: activee,
  });
}

/**
 * Calendrier d'un tiers, en lecture seule : planning de l'organisme et
 * événements non personnels. Les événements « Personnel » et Google Calendar
 * de la personne ne sortent jamais de l'API.
 */
export function useCalendrierConsulte(
  cible: PersonneCalendrier | null,
  debut: string,
  fin: string,
) {
  return useQuery({
    queryKey: [...RACINE, 'consultation', cible?.type, cible?.id, debut, fin],
    queryFn: () =>
      appelApi<CalendrierConsulte>(
        `/calendrier/personnes/${cible?.id}/evenements?type=${cible?.type}` +
          `&debut=${encodeURIComponent(debut)}&fin=${encodeURIComponent(fin)}`,
      ),
    enabled: cible !== null,
  });
}

// ---------------------------------------------------------------------------
// Planning de l'organisme
// ---------------------------------------------------------------------------

/**
 * Séances, alternance, périodes en entreprise et échéances, tels que l'API les
 * accorde au profil connecté. C'est la seule source du calendrier pour tout ce
 * qui n'est pas saisi à la main : l'écran ne choisit pas ce qu'il montre, il
 * affiche ce que le serveur a bien voulu rendre.
 */
export type PerimetrePlanning =
  | 'PERSONNEL'
  | 'INTERVENANT'
  | 'ETENDU'
  | 'ENTREPRISES'
  | 'ENTRETIENS_ET_ENTREPRISES'
  | 'AUCUN';

export type NaturePlanning =
  | 'SEANCE'
  | 'ECHEANCE'
  | 'PROGRAMMATION'
  | 'ALTERNANCE_CENTRE'
  | 'ALTERNANCE_ENTREPRISE'
  | 'IMMERSION'
  | 'ENTRETIEN';

export interface DetailPlanning {
  formation?: string;
  groupe?: string;
  formateur?: string;
  site?: string;
  salle?: string;
  type?: string;
  statut?: string;
  entreprise?: string;
  inscrits?: number;
}

export interface EvenementPlanning {
  id: string;
  titre: string;
  nature: NaturePlanning;
  date: string;
  heureDebut: string | null;
  heureFin: string | null;
  lieu: string | null;
  typeJour?: TypeJour;
  detail: DetailPlanning;
}

export interface OptionFiltre {
  valeur: string;
  libelle: string;
}

export interface FiltresPlanning {
  sites: OptionFiltre[];
  formations: OptionFiltre[];
  groupes: OptionFiltre[];
  formateurs: OptionFiltre[];
}

export interface ReponsePlanning {
  perimetre: PerimetrePlanning;
  explication: string;
  evenements: EvenementPlanning[];
  filtres: FiltresPlanning;
}

export interface CriteresPlanning {
  site: string;
  formation: string;
  groupe: string;
  formateur: string;
}

const PERIODE_RELECTURE_PLANNING_MS = 5 * 60_000;

export function usePlanningCalendrier(
  debut: string,
  fin: string,
  criteres: CriteresPlanning,
  activee = true,
) {
  const parametres = new URLSearchParams({ debut, fin });
  // Les critères ne sont que des réductions : l'API borne déjà le périmètre au
  // profil, et refuse un site qui n'y est pas plutôt que de l'ignorer.
  if (criteres.site) parametres.set('site', criteres.site);
  if (criteres.formation) parametres.set('formation', criteres.formation);
  if (criteres.groupe) parametres.set('groupe', criteres.groupe);
  if (criteres.formateur) parametres.set('formateur', criteres.formateur);

  return useQuery({
    queryKey: [
      ...RACINE,
      'planning',
      debut,
      fin,
      criteres.site,
      criteres.formation,
      criteres.groupe,
      criteres.formateur,
    ],
    queryFn: () => appelApi<ReponsePlanning>(`/calendrier/planning?${parametres.toString()}`),
    enabled: activee,
    // Mise à jour automatique (TF-452) : un planning modifié ailleurs — séance
    // ouverte, classe programmée, programmation revue — apparaît au retour sur
    // l'onglet ou dans les cinq minutes, sans recharger la page.
    refetchOnWindowFocus: true,
    refetchInterval: PERIODE_RELECTURE_PLANNING_MS,
  });
}

/**
 * Abonnement de l'agenda personnel au planning (TF-452) : le lien iCal que
 * Google Agenda, Outlook ou Apple relisent d'eux-mêmes. Tout vient de l'API —
 * le refus d'un profil sans planning personnel, les liens d'ajout de chaque
 * agenda, la dernière lecture.
 */
export interface LiensAbonnement {
  ical: string;
  webcal: string;
  google: string;
  outlook: string;
  outlook365: string;
}

export interface EtatAbonnement {
  refus: string | null;
  nomCalendrier: string;
  abonnement: {
    /** `null` : lien illisible côté serveur, à renouveler pour le revoir. */
    liens: LiensAbonnement | null;
    creeLe: string;
    renouveleLe: string | null;
    dernierAccesLe: string | null;
  } | null;
}

const CLE_ABONNEMENT = ['calendrier', 'abonnement'] as const;

export function useAbonnementCalendrier(activee = true) {
  return useQuery({
    queryKey: CLE_ABONNEMENT,
    queryFn: () => appelApi<EtatAbonnement>('/calendrier/abonnement'),
    enabled: activee,
  });
}

/** Création, renouvellement ou désactivation : la réponse est le nouvel état. */
export function useGesteAbonnement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (geste: 'CREER' | 'RENOUVELER' | 'DESACTIVER') =>
      geste === 'CREER'
        ? appelApi<EtatAbonnement>('/calendrier/abonnement', { methode: 'POST' })
        : geste === 'RENOUVELER'
          ? appelApi<EtatAbonnement>('/calendrier/abonnement/renouvellement', { methode: 'POST' })
          : appelApi<EtatAbonnement>('/calendrier/abonnement', { methode: 'DELETE' }),
    onSuccess: (etat) => client.setQueryData(CLE_ABONNEMENT, etat),
    // Un 409 dit que l'écran était en retard : on relit l'état réel.
    onError: () => client.invalidateQueries({ queryKey: CLE_ABONNEMENT }),
  });
}
